import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { FaTrash, FaPlus, FaVideo, FaImage } from "react-icons/fa";
import { motion } from "framer-motion";
import { server } from "../server";
import { uploadMediaToServer } from "../utils/uploadMediaToServer";
import Loading from "../Components/global/Loading";
import {
  getAllCategoriesList,
  getAllGalleryMedias,
} from "../redux/actions/contents";
import { getYoutubeEmbedUrl } from "../utils/youTube";
import { deleteMediaFromServer } from "../utils/deleteMediaFromServer";
import PageName from "../Components/global/PageName";
import compressImage from "../utils/compressAsset";

export default function GalleryManager() {
  const dispatch = useDispatch();
  const {
    galleryList,
    categoriesList,
    loading: galleryLoading,
  } = useSelector((state) => state?.content);
  const { register, handleSubmit, watch, reset } = useForm();
  const [preview, setPreview] = useState(null);
  const mediaType = watch("mediaType");
  const [loading, setLoading] = useState(false);
  const [sync, setSync] = useState(0);

  useEffect(() => {
    dispatch(getAllGalleryMedias(1, 100));
    dispatch(getAllCategoriesList(1, 100));
  }, [dispatch, sync]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      let mediaUrl = data.mediaUrl;

      if (data?.mediaType === "image") {
        const file = await compressImage(data.media[0]);
        mediaUrl = await uploadMediaToServer(file, "null");
      }

      const { data: resData } = await axios.post(
        `${server}/gallery/new`,
        null,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            categoryId: data.categoryId,
            mediaType: data.mediaType,
            caption: data.caption,
            mediaUrl,
          },
        }
      );

      toast.success(resData?.message || "Gallery item uploaded successfully!");
      reset({ mediaUrl: "", caption: "" });
      setPreview(null);
      setSync((prev) => prev + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, imageUrl) => {
    const userRes = window.confirm(
      "Are you sure you want to delete this gallery artwork?"
    );
    if (!userRes) return;

    try {
      setLoading(true);
      await deleteMediaFromServer(imageUrl);
      await axios.delete(`${server}/gallery/deleteById`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          mediaId: id,
        },
      });
      toast.success("Gallery item deleted.");
      setSync((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-12">
      <PageName
        title="Gallery Manager"
        subTitle="Upload portfolio high-res photos and video embeds."
      />

      <div className="max-w-7xl mx-auto px-4 mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Upload Form */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 sticky top-24">
              <h3 className="text-base font-bold text-slate-800 mb-1 flex items-center gap-2">
                <FaImage className="text-amber-600" /> Add Portfolio Artwork
              </h3>
              <p className="text-xs text-slate-500 mb-6">
                Categorize your work and upload photos or YouTube reels.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Category Selection */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Select Category
                  </label>
                  <select
                    {...register("categoryId", { required: true })}
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-xl focus:outline-none focus:border-amber-600 bg-white"
                  >
                    <option value="">Select Category</option>
                    {categoriesList?.map((e, i) => (
                      <option value={e?.id} key={i}>
                        {e?.category}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Media Type Selection */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Media Type
                  </label>
                  <select
                    {...register("mediaType", { required: true })}
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-xl focus:outline-none focus:border-amber-600 bg-white"
                  >
                    <option value="">Select Media Type</option>
                    <option value="image">Image Photo</option>
                    <option value="video">YouTube Video Reel</option>
                  </select>
                </div>

                {/* File Upload for Image */}
                {mediaType === "image" && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Upload Photo Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      {...register("media", { required: true })}
                      className="w-full text-xs p-2.5 border border-slate-300 rounded-xl focus:outline-none focus:border-amber-600"
                      onChange={(e) =>
                        e.target.files?.[0] &&
                        setPreview(URL.createObjectURL(e.target.files[0]))
                      }
                    />
                    {preview && (
                      <img
                        src={preview}
                        alt="Preview"
                        className="w-full h-40 object-cover mt-3 rounded-xl border border-slate-200"
                      />
                    )}
                  </div>
                )}

                {/* URL Input for Video */}
                {mediaType === "video" && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      YouTube Video Link
                    </label>
                    <input
                      {...register("mediaUrl", { required: true })}
                      className="w-full text-xs p-2.5 border border-slate-300 rounded-xl focus:outline-none focus:border-amber-600"
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                )}

                {/* Caption */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Caption / Title
                  </label>
                  <input
                    {...register("caption")}
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-xl focus:outline-none focus:border-amber-600"
                    placeholder="Enter caption or artwork name..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs tracking-wider uppercase shadow-md shadow-amber-600/20 transition-all disabled:opacity-50 mt-4"
                >
                  <FaPlus size={12} /> Add to Gallery
                </button>
              </form>
            </div>
          </div>

          {/* Gallery Media Grid */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-base font-bold text-slate-900">
                    Gallery Artworks
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Total Items: <span className="font-semibold text-amber-600">{galleryList?.length || 0}</span>
                  </p>
                </div>
              </div>

              {galleryList?.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-xs">
                  No gallery items found. Upload artworks using the form on the left.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {galleryList?.map((item, index) => (
                    <motion.div
                      key={item?.id || item?._id || index}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.04 }}
                      className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden relative group hover:shadow-md transition-all flex flex-col justify-between"
                    >
                      <button
                        className="absolute top-2 right-2 text-white bg-red-600 hover:bg-red-700 p-2 rounded-full shadow-md z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDelete(item?.id, item?.mediaUrl)}
                        title="Delete Media"
                      >
                        <FaTrash size={12} />
                      </button>

                      {item.mediaType === "video" ? (
                        <div className="relative aspect-video bg-black flex items-center justify-center">
                          <iframe
                            src={getYoutubeEmbedUrl(item?.mediaUrl)}
                            className="w-full h-full border-0 pointer-events-none"
                            title={item.caption || "YouTube Reel"}
                          />
                          <div className="absolute top-2 left-2 bg-black/60 text-amber-400 p-1.5 rounded-md">
                            <FaVideo size={12} />
                          </div>
                        </div>
                      ) : (
                        <div className="relative aspect-square overflow-hidden bg-slate-100">
                          <img
                            src={item.mediaUrl}
                            alt={item.caption || "Gallery"}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      )}

                      <div className="p-3 bg-white border-t border-slate-100">
                        <p className="text-xs font-semibold text-slate-800 truncate">
                          {item.caption || "Untitled Artwork"}
                        </p>
                        <span className="text-[0.62rem] text-amber-700 uppercase font-medium tracking-wider block mt-0.5">
                          {item.categoryName || "Bridal Gallery"}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {(loading || galleryLoading) && <Loading />}
    </div>
  );
}
